import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { api, billsApi } from '../lib/api';
import { useList } from '../hooks/useList';
import { useCount } from '../hooks/useCount';
import { useStatFilter } from '../hooks/useStatFilter';
import { StatTile, FilterNote } from '../components/charts/StatTile';
import { DataTable, type Column } from '../components/ui/DataTable';
import { PageHeader, Badge, Money, DateText, ActionMenu } from '../components/ui';
import { ListFilter } from '../components/ui/FilterMenu';

import type { Bill, BillLocations } from '../types/api';

const STATUSES = ['DRAFT', 'APPROVED', 'PART_PAID', 'PAID', 'VOID'] as const;
const statusLabel = (s: string) => s.replace('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/** "Beechwood Grove · East Wing" — unless the building's own name already starts with the property's. */
const buildingLabel = (b: { name: string; propertyName: string }) => (b.name.startsWith(b.propertyName) ? b.name : `${b.propertyName} · ${b.name}`);
/** Within one property: "East Wing" for "Beechwood Grove — East Wing". */
const shortBuilding = (b: { name: string; propertyName: string }) =>
  (b.name.startsWith(b.propertyName) ? b.name.slice(b.propertyName.length).replace(/^\s*[—–·-]\s*/, '') : b.name) || b.name;

const FEE: Record<string, string> = { LETTING: 'Letting fee', RENEWAL: 'Renewal fee', WORKS: 'Works arranged' };
/** Marks an agent's invoice (Task G) among the contractor bills. */
export const AgentChip = () => <span className="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wide text-violet-700">Agent</span>;

/** A bill above the sign-off limit that no director has signed off yet. */
const awaitingSignoff = (b: Pick<Bill, 'requiresSignoff' | 'signedOffBy' | 'status'>) =>
  b.requiresSignoff && !b.signedOffBy && b.status !== 'VOID' && b.status !== 'PAID';

/**
 * Accounts payable. Most bills arrive by themselves: closing a work order with an actual
 * cost raises a draft. A row opens the bill's own page (/app/bills/:id: view, edit a draft, approve, pay, void); "New bill"
 * opens /app/bills/new (VAT-inclusive prices, lines from the items catalogue). The ⋮ on each row offers the same actions.
 * Approve posts the liability; paying draws on the property's own balance first and the group company advances any shortfall.
 */
/** The tiles at the top: each is a filter on the table (prototype §38). Keys match the Dashboard's drill paths. */
const TILES = {
  draft: { label: 'Drafts to approve', params: { status: 'DRAFT' } },
  unpaid: { label: 'Approved, to pay', params: { unpaid: true } },
  signoff: { label: 'Awaiting sign-off', params: { awaitingSignoff: true } },
  agent: { label: 'Agent invoices', params: { kind: 'AGENT' } },
} as const;
type TileKey = keyof typeof TILES;
/** Filters a link can open the list on without a tile of their own: Accounts' "Agent invoices waiting" (?filter=agentDraft). */
const DRILLS = { ...TILES, agentDraft: { label: 'Agent invoices to approve', params: { kind: 'AGENT', status: 'DRAFT' } } } as const;
type DrillKey = keyof typeof DRILLS;

export default function BillsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tf = useStatFilter<DrillKey>();
  const tile = tf.active && DRILLS[tf.active] ? DRILLS[tf.active] : null;
  const list = useList<Bill>('/bills', {}, { extra: tile?.params });
  const counts = {
    draft: useCount('/bills', TILES.draft.params), unpaid: useCount('/bills', TILES.unpaid.params),
    signoff: useCount('/bills', TILES.signoff.params), agent: useCount('/bills', TILES.agent.params),
  };
  const [error, setError] = useState<string | null>(null);
  // Pay opens the bill on its Payments tab; Void opens it with the void dialog up.
  const open = (b: Bill, flag?: 'pay' | 'void') => navigate(`/app/bills/${b.id}${flag === 'pay' ? '?tab=payments' : flag === 'void' ? '?void=1' : ''}`);
  const approve = useMutation({
    mutationFn: async (id: string) => billsApi.approve(id),
    onMutate: () => setError(null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['/bills'] }),
    onError: (e: Error) => setError(e.message),
  });

  // Filter choices. Buildings and units come from /bills/locations (bills.read), so finance needs no buildings.read.
  const propertyId = typeof list.filters.propertyId === 'string' ? list.filters.propertyId : undefined;
  const buildingId = typeof list.filters.buildingId === 'string' ? list.filters.buildingId : undefined;
  const properties = useQuery({ queryKey: ['properties-picker'], queryFn: async () => (await api.get<Array<{ id: string; name: string }>>('/properties', { perPage: 200 })).data });
  const allBuildings = useQuery({ queryKey: ['/bills/locations'], queryFn: async () => (await api.get<BillLocations>('/bills/locations')).data.buildings });
  // Units need a property: the chosen one, else the chosen building's.
  const unitProperty = propertyId ?? allBuildings.data?.find((b) => b.id === buildingId)?.propertyId;
  const units = useQuery({
    queryKey: ['/bills/locations', unitProperty, buildingId ?? null],
    queryFn: async () => (await api.get<BillLocations>('/bills/locations', { propertyId: unitProperty, buildingId })).data.units,
    enabled: !!unitProperty,
  });
  const buildingOptions = useMemo(() => (allBuildings.data ?? [])
    .filter((b) => !propertyId || b.propertyId === propertyId)
    .map((b) => ({ value: b.id, label: propertyId ? shortBuilding(b) : buildingLabel(b) })), [allBuildings.data, propertyId]);
  const unitOptions = useMemo(() => (units.data ?? []).map((u) => ({ value: u.id, label: u.reference })), [units.data]);

  /** Apply the Filter menu; a building or unit that does not belong to the newly chosen property (or building) is dropped. */
  const applyFilters = (next: Record<string, unknown>) => {
    const out = { ...next };
    const nextProperty = typeof out.propertyId === 'string' ? out.propertyId : undefined;
    const nextBuilding = typeof out.buildingId === 'string' ? out.buildingId : undefined;
    if (nextProperty !== propertyId && nextProperty) {
      const b = allBuildings.data?.find((x) => x.id === nextBuilding);
      if (nextBuilding && b?.propertyId !== nextProperty) out.buildingId = undefined;
      if (out.unitId && !units.data?.some((u) => u.id === out.unitId && u.propertyId === nextProperty)) out.unitId = undefined;
    }
    const keptBuilding = typeof out.buildingId === 'string' ? out.buildingId : undefined;
    if (keptBuilding && keptBuilding !== buildingId && out.unitId) {
      const u = units.data?.find((x) => x.id === out.unitId);
      if (!u || u.buildingId !== keptBuilding) out.unitId = undefined;
    }
    list.setFilters(out);
  };

  const columns: Column<Bill>[] = [
    { key: 'number', label: 'Bill', render: (b) => (
      <div className="flex flex-col items-start gap-1">
        <span className="inline-flex items-center gap-1.5">
          <span className="font-mono text-[12px] text-ink-600">{b.number}</span>
          {b.attachmentCount > 0 && <span title={`${b.attachmentCount} document${b.attachmentCount === 1 ? '' : 's'} attached`} aria-label={`${b.attachmentCount} attached`} className="inline-flex items-center gap-0.5 text-[11px] tabular-nums text-ink-400"><Paperclip className="h-3 w-3" />{b.attachmentCount}</span>}
        </span>
        {b.kind === 'AGENT' && <AgentChip />}
        {awaitingSignoff(b) && <Badge value="Sign-off" tone="warning" />}
      </div>) },
    { key: 'vendorName', label: 'Vendor · property · unit', render: (b) => (
      // Capped so the row menu stays on screen; the full line is in the tooltip.
      <div className="max-w-[260px]">
        <div className="truncate font-medium text-ink-800">{b.kind === 'AGENT' ? `${b.agentName ?? 'Agent'} · ${FEE[b.agentFeeType ?? 'LETTING']}` : b.vendorName ?? 'No vendor'}</div>
        <div className="truncate text-[12px] text-ink-500" title={[b.propertyName, b.unitReference, b.workOrderReference, b.tenancyReference].filter(Boolean).join(' · ')}>{b.propertyName}{b.unitReference ? ` · ${b.unitReference}` : ''}{b.workOrderReference ? ` · ${b.workOrderReference}` : ''}{b.tenancyReference ? ` · ${b.tenancyReference}` : ''}</div>
      </div>) },
    { key: 'billDate', label: 'Date', render: (b) => <DateText value={b.billDate} />, hideOnMobile: true },
    { key: 'dueDate', label: 'Due', render: (b) => <span className={b.isOverdue ? 'font-medium text-red-600' : ''}><DateText value={b.dueDate} /></span>, hideOnMobile: true },
    { key: 'totalMinor', label: 'Total', align: 'right', render: (b) => <Money minor={b.totalMinor} currency={b.currency} /> },
    { key: 'outstandingMinor', label: 'Outstanding', align: 'right', hideOnMobile: true, render: (b) => <Money minor={b.status === 'VOID' ? 0 : b.outstandingMinor} currency={b.currency} className={b.outstandingMinor > 0 && b.status !== 'VOID' ? 'font-medium text-ink-800' : 'text-ink-400'} /> },
    { key: 'status', label: 'Status', render: (b) => <Badge value={b.status} /> },
    { key: 'actions', label: '', sortable: false, width: '48px', render: (b) => (
      <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
        <ActionMenu vertical label={`Actions for ${b.number}`} items={[
          { label: 'Open', icon: 'ArrowUpRight', onClick: () => open(b) },
          b.status === 'DRAFT' && { label: 'Approve', icon: 'Check', can: 'bills.approve', disabled: approve.isPending, onClick: () => approve.mutate(b.id), title: 'Posts the liability to the ledger' },
          (b.status === 'APPROVED' || b.status === 'PART_PAID') && b.outstandingMinor > 0 && { label: 'Pay', icon: 'Banknote', can: 'bills.pay', onClick: () => open(b, 'pay') },
          (b.status === 'DRAFT' || b.status === 'APPROVED') && b.paidMinor === 0 && { label: 'Void', icon: 'Ban', can: 'bills.void', danger: true, separator: true, onClick: () => open(b, 'void') },
        ]} />
      </div>
    ) },
  ];

  return (
    <div className="space-y-2.5">
      <PageHeader title="Bills"
        menu={[{ label: 'New bill', icon: 'Plus', can: 'bills.create', onClick: () => navigate('/app/bills/new') }, { label: 'Export CSV', export: { path: '/bills', page: 'bills', query: list.query } }]} />
      <div className="tile-row">
        {(Object.keys(TILES) as TileKey[]).map((k) => (
          <StatTile key={k} compact label={TILES[k].label} value={counts[k] ?? '—'} status={k === 'signoff' && counts.signoff ? 'warning' : undefined} {...tf.tile(k)} />
        ))}
      </div>
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{error}</p>}
      {tile && <FilterNote label={tile.label} count={list.meta?.total} onClear={tf.clear} />}
      <DataTable columns={columns} rows={list.rows} meta={list.meta} page={list.page} onPage={list.setPage} isLoading={list.isLoading} isFetching={list.isFetching}
        rowKey={(b) => b.id} onRowClick={(b) => open(b)} search={list.search} onSearch={list.setSearch} searchPlaceholder="Number, vendor, property, job…"
        emptyTitle="No bills" emptyBody="Closing a work order with an actual cost raises a draft bill here."
        filters={<ListFilter filters={list.filters} setFilters={applyFilters} groups={[
          { key: 'kind', label: 'Kind', options: [{ value: 'SUPPLIER', label: 'Contractors' }, { value: 'AGENT', label: 'Agents & letting' }] },
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ value: s, label: statusLabel(s) })) },
          { key: 'requiresSignoff', label: 'Needs sign-off', as: 'boolean', options: [{ value: 'true', label: 'Above the sign-off limit' }] },
          { key: 'propertyId', label: 'Property', options: (properties.data ?? []).map((p) => ({ value: p.id, label: p.name })) },
          { key: 'buildingId', label: 'Building', options: buildingOptions },
          { key: 'unitId', label: 'Unit', options: unitOptions },
        ]} />} />
    </div>
  );
}
